export type ArchitectureTabId = "General" | "Tier1" | "Tier2" | "Tier3" | "DesignPreferences" | "Demand" | "BOM";

export interface ArchitectureTabHeader {
  id: ArchitectureTabId;
  label: string;
  tierNum?: number;
}

export interface ArchitectureTabOptions {
  canEditFlexNap?: boolean;
  showDesignPreferences?: boolean;
}

const ARCHITECTURE_TABS: ArchitectureTabHeader[] = [
  { id: "General", label: "General" },
  { id: "Tier1", label: "Drop Tier", tierNum: 1 },
  { id: "Tier2", label: "Distribution Tier", tierNum: 2 },
  { id: "Tier3", label: "Feeder Tier", tierNum: 3 },
  { id: "DesignPreferences", label: "Design Preferences" },
  { id: "Demand", label: "Demand Model" },
  { id: "BOM", label: "BOM" },
];

/**
 * The architecture editor tabs shown for an architecture with `numberOfTiers` tiers.
 * The BOM tab is hidden for accounts that can edit FlexNAP architectures, and the
 * Design Preferences tab is only shown for Planner Extended projects.
 */
export const getVisibleTabs = (
  numberOfTiers: number,
  { canEditFlexNap = false, showDesignPreferences = false }: ArchitectureTabOptions = {}
): ArchitectureTabHeader[] =>
  ARCHITECTURE_TABS.filter((t) => t.tierNum == null || t.tierNum <= numberOfTiers)
    .filter((t) => t.id !== "BOM" || !canEditFlexNap)
    .filter((t) => t.id !== "DesignPreferences" || showDesignPreferences);

const tabOrder = (id: ArchitectureTabId): number => ARCHITECTURE_TABS.findIndex((t) => t.id === id);

/**
 * Whether tab `a` comes after tab `b` in the architecture editor.
 */
export const isTabAfter = (a: ArchitectureTabId, b: ArchitectureTabId): boolean => tabOrder(a) > tabOrder(b);

/**
 * The tab following `id` in `tabs`, if any.
 */
export const getNextTabId = (tabs: ArchitectureTabHeader[], id: ArchitectureTabId): ArchitectureTabId | undefined =>
  tabs[tabs.findIndex((t) => t.id === id) + 1]?.id;

/**
 * The id of the last tab in `tabs`.
 */
export const getLastTabId = (tabs: ArchitectureTabHeader[]): ArchitectureTabId => tabs[tabs.length - 1].id;
